import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import { AddOutlined, ChevronLeft, CloseOutlined, Delete as DeleteIcon, Launch as LaunchIcon } from '@mui/icons-material';
import DownIcon from '@mui/icons-material/KeyboardArrowDown';
import UpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useApi } from 'context/ApiProvider';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { showAlertDialog } from 'components/Dialog/dialog';
import { fetchAdminDesigns, fetchHubDesigns, fetchUserSelectItems } from 'services/adminService';
import { AdminUserSelectItem } from 'models/AdminUserSelectItem';
import { DesignSortType, PaginatedDesignSearchRequestDto } from 'models/PaginatedDesignSearchRequestDto';
import { SaveSettings, scheduleAutosave } from 'components/Viewer3d/SaveManager';
import { getNewDesign } from 'services/designService';
import { useViewerStore } from 'context/ViewerContext';
import { useAuth } from 'context/AuthContext';
import { PaginatedUserSearchRequestDto } from 'models/PaginatedUserSearchRequestDto';
import { LOCAL_STORAGE_KEYS } from 'components/Viewer3d/consts';
import { useAdminStore } from 'context/AdminContext';
import { formatLocalDate } from 'Utils/StringUtils';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickerValidDate } from '@mui/x-date-pickers/models';



const DesignsTable: React.FC = () => {
  var authenticatedUser = useAuth().user;
  const { isDarkMode } = useAppTheme();
  const api = useApi();
  const navigate = useNavigate();

  const [designData, setDesignData] = useState<AdminDesignData[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const searchTerm = useAdminStore((state) => state.designSearchTerm);
  const setSearchTerm = useAdminStore((state) => state.setDesignSearchTerm);
  const [showFilters, setShowFilters] = useState(true);
  const [fromDate, setFromDate] = useState<PickerValidDate | null>(null);
  const [toDate, setToDate] = useState<PickerValidDate | null>(null);
  const selectedUserIdFilter = useAdminStore((state) => state.designSelectedUserIdFilter);
  const setSelectedUserIdFilter = useAdminStore((state) => state.setDesignSelectedUserIdFilter);
  const [userSelectItems, setUserSelectItems] = useState<AdminUserSelectItem[]>([]);
  const onlyFinalizedFilter = useAdminStore((state) => state.designOnlyFinalizedFilter);
  const setOnlyFinalizedFilter = useAdminStore((state) => state.setDesignOnlyFinalizedFilter);
  const [isOnlyFinalizedFilterEnabled, SetOnlyFinalizedFilterEnabled] = useState<boolean>(true);
  const [isCreateButtonEnabled, setCreateButtonEnabled] = useState<boolean>(true);
  const isPrintedFilter = useAdminStore((state) => state.designIsPrintedFilter);
  const setPrintedFilter = useAdminStore((state) => state.setDesignIsPrintedFilter);
  const [isPrintedFilterEnabled, SetPrintedFilterEnabled] = useState<boolean>(false);
  const pageSize = 10;
  const totalPages = useRef(1);
  const selectedDesignId = useSelectedDesignStore.getState().designId;
  const pageNumber = useAdminStore((state) => state.designPageNumber);
  const setPageNumber = useAdminStore((state) => state.setDesignPageNumber);
  const [sortColumnName, setSortColumnName] = useState<DesignSortType>(null);
  const [sortDesc, setSortDesc] = useState<boolean | null>(null);

  const applyUserAccess = () => {
    if (authenticatedUser?.group?.IsHub) {
      setCreateButtonEnabled(false)
    } else {
      setCreateButtonEnabled(true);
    }
  }

  useEffect(() => {

    if (!authenticatedUser) return
    if (!api) return;

    const fetchUserItemsReqDto = new PaginatedUserSearchRequestDto();
    if (authenticatedUser?.group && !authenticatedUser.group.IsAll) {
      fetchUserItemsReqDto.groupName = authenticatedUser.group.name;
    }
    fetchUserSelectItems(api, fetchUserItemsReqDto)
      .then((resUserList) => {
        if (!resUserList) return;
        setUserSelectItems(resUserList);
      }).catch((err) => {
        console.error(err);
      });
    applyUserAccess();
  }, [api]);

  useEffect(() => {
    const fetchDesigns = async () => {
      if (!api) return;
      const isAllUser = authenticatedUser?.group?.IsAll === true;
      const effectiveOnlyFinalizedFilter = isAllUser ? onlyFinalizedFilter : true;
      const effectivePrintedFilter = isAllUser ? isPrintedFilter : null;
      setOnlyFinalizedFilter(effectiveOnlyFinalizedFilter);

      try {
        setLoading(true);
        const designRequestDto = new PaginatedDesignSearchRequestDto({
          pageNumber,
          pageSize,
          searchTerm,
          userId: !selectedUserIdFilter ? null : parseInt(selectedUserIdFilter),
          finalized: effectiveOnlyFinalizedFilter ? true : null, // never send "false" to server, we'll never need to see "only" unfinished designs
          printed: effectivePrintedFilter,
          createdFrom: fromDate?.toISOString(),
          createdTo: toDate?.toISOString(),
          sortBy: sortColumnName,
          sortDescending: sortDesc,
        });
        let res;
        if (!authenticatedUser?.group?.IsHub) {
          res = await fetchAdminDesigns(api, designRequestDto);
        } else {
          res = await fetchHubDesigns(api, designRequestDto);
        }
        if (!res) {
          setError('No response from server!');
          totalPages.current = 0;
          setDesignData([]);
          setHasNextPage(false);
          return;
        }
        if (!res?.data || res.data.length < 1) {
          setError('No designs found for this page.');
          totalPages.current = 0;
          setDesignData([]);
          setHasNextPage(false);
          return;
        }
        totalPages.current = res.totalPages;
        setSelected(selectedDesignId);
        setPageNumber(res.pageNumber);
        setHasNextPage(res.pageNumber < res.totalPages);

        const mappedData: AdminDesignData[] = res.data.map((item: AdminApiDesignData) => ({
          id: item.designId,
          name: item.name,
          age: null,
          date: formatLocalDate(item.createdAt),
          sendDate: item.sendDate ? formatLocalDate(item.sendDate) : null,
          status: (item.hasPressure ? 'Pressure' : item.hasRondom ? 'Rondom Pressure' : '')
            + ((item.hasPressure || item.hasRondom) && item.hasWBCT ? ' & ' : '')
            + (item.hasWBCT ? ' WBCT' : '')
            + (item.hasPressure || item.hasRondom || item.hasWBCT ? '' : 'Manual'),
          email: item.email,
          printCount: item.printCount,
          finalized: item.finalized,
          hasPressure: item.hasPressure,
          hasRondom: item.hasRondom,
          hasWBCT: item.hasWBCT,
          taskId: item.taskId
        }));
        // mappedData.sort((a, b) => {
        //   const printCountDiff = a.printCount - b.printCount;
        //   return printCountDiff != 0 ? printCountDiff : b.id - a.id;
        // });
        setDesignData(mappedData);
        setError(null);
      } catch (err) {
        setError('Failed to fetch designs. Please try again later.');
        console.error(err);
        totalPages.current = 0;
        setDesignData([]);
        setHasNextPage(false);
      } finally {
        setLoading(false);
      }
    };
    fetchDesigns();
  }, [api, pageNumber, searchTerm, selectedUserIdFilter, onlyFinalizedFilter, isPrintedFilter, authenticatedUser, fromDate, toDate, sortColumnName, sortDesc]);

  const handleRowClick = (id: number) => {
    setSelected(id === selected ? null : id);
  };

  const isSelected = (id: number) => id === selected;

  const handleOpenDesign = async (id: number, taskId: number = -1) => {
    const selectedDesign = designData.find(d => d.id === id);
    if (selectedDesign) {
      const status = selectedDesign.status.toLocaleLowerCase();
      if (status.includes('rondom')) {
        useSelectedDesignStore.setState({ hasRondomImage: true });
      } else if (status.includes('pressure')) {
        useSelectedDesignStore.setState({ hasPressure: true });
      } else {
        useSelectedDesignStore.setState({ hasPressure: false });
        useSelectedDesignStore.setState({ hasRondomImage: false });
      }
    }
    useSelectedDesignStore.setState({ designId: id });
    useSelectedDesignStore.setState({ printTaskId: taskId });
    // Yield to event loop to allow state updates to propagate before navigating away 
    // and avoid loading previous design
    await new Promise(resolve => setTimeout(resolve, 0));
    navigate('/');
  };

  const handleNextPage = () => {
    setPageNumber(pageNumber === null ? 1 : pageNumber + 1);
  };

  const handlePreviousPage = () => {
    if (pageNumber && pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleDeleteDesign = async (id: number) => {
    setSelected(id);
    try {
      const confirmed = await showAlertDialog({
        title: 'Remove Design',
        description: 'Are you sure you want to remove this design?',
        agreeText: 'Yes',
        disagreeText: 'No',
      });
      if (!confirmed) return;

      const res = await api.delete(`/api/designs/${id}`);
      if (!res.ok) throw new Error('Failed to delete design');

      setDesignData(designData.filter((design) => design.id !== id));
      if (selected === id) setSelected(null);
      toast.success('Design deleted successfully', { duration: 4000 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete design', { duration: 4000 });
    }
  };

  const handleUserSelectChange = async (userId: string) => {
    setSelectedUserIdFilter(userId ?? '');
  }

  const handleSelectPage = async (pageNumber: any) => {
    setPageNumber(pageNumber);
  }

  const handleNewDesign = () => {
    if (designData.length === 0) {
      newDesignClicked();
    } else {
      scheduleAutosave('Current design', {
        now: true
      } as Partial<SaveSettings>).then(() => {
        newDesignClicked().then(() => {
          // Yield to event loop to allow state updates to propagate before navigating away 
          // and avoid loading previous design
          new Promise(resolve => setTimeout(resolve, 0)).then(() => {
            navigate('/');
          });
        })
      });
    }
  }

  const newDesignClicked = async () => {
    setPageNumber(1);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);

    try {
      const templateName = 'generic';
      await getNewDesign(api, templateName).then((dId: number) => {
        if (dId < 1) return;
        setSelected(dId);
        useSelectedDesignStore.setState({ designId: dId });
        useSelectedDesignStore.setState({ designUpdated: true });
        useViewerStore.setState({ newDesign: true });
        useSelectedDesignStore.setState({ hasPressure: false });
        useSelectedDesignStore.setState({ hasRondomImage: false });
        let storageData = { designId: dId, designPageNumber: pageNumber };
        localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN, JSON.stringify(storageData));
      });
    } catch (error) {
      console.error('Error Opening new template design', error);
    }
  }

  const changeSort = async (column: DesignSortType) => {
    if (column !== sortColumnName) {
      setSortDesc(false);
    } else {
      setSortDesc(!sortDesc);
    }
    setSortColumnName(column);
  }

  const handleClearSort = async () => {
    setSortColumnName(null);
    setSortDesc(null);
  }

  return (
    <div style={{ padding: 0, backgroundColor: isDarkMode ? '#1e1e1e' : '#f5f5f5' }}>
      <div className="admin-toolbar">
        <TextField
          className="admin-search"
          placeholder="Search Designs..."
          value={searchTerm ?? ''}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ opacity: 0.8 }} />
                </InputAdornment>
              ),
            }
          }}
          sx={{
            flex: '1 1 200px',
            minWidth: 0,
            '& .MuiInputBase-root': {
              borderRadius: '4px',
              background: 'var(--input-bg)',
              color: 'var(--text-color)',
              fontSize: '0.9rem',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              padding: '2px 10px',
            },
            '& .MuiInputBase-input': { padding: '8px 8px 8px 0' },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--input-border)',
            }
          }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, flex: '1 1 260px' }}>
          <DatePicker
            label="Start Date"
            value={fromDate}
            onChange={(newValue) => setFromDate(newValue)}
            maxDate={toDate ?? undefined}
            slotProps={{
              textField: {
                size: 'small',
                sx: { flex: '1 1 140px', minWidth: 0 },
              }
            }}
            sx={{
              flex: '1 1 140px',
              minWidth: 0,
              background: 'var(--input-bg)',
              borderRadius: '4px',
              label: {
                color: "var(--text-color)"
              },
              span: {
                color: "var(--text-color)"
              },
              '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline': {
                borderColor: 'var(--input-border)',
              }
            }}
          />
          <DatePicker
            label="End Date"
            value={toDate}
            onChange={(newValue) => setToDate(newValue)}
            minDate={fromDate ?? undefined}
            slotProps={{
              textField: {
                size: 'small',
                sx: { flex: '1 1 140px', minWidth: 0 },
              }
            }}
            sx={{
              flex: '1 1 140px',
              minWidth: 0,
              background: 'var(--input-bg)',
              borderRadius: '4px',
              label: {
                color: "var(--text-color)"
              },
              span: {
                color: "var(--text-color)"
              },
              '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline': {
                borderColor: 'var(--input-border)',
              }
            }}
          />
          </Box>
        </LocalizationProvider>
        {isCreateButtonEnabled && <Button
          variant="outlined"
          onClick={handleNewDesign}
          startIcon={<AddOutlined />}
          sx={{
            border: '1px solid var(--input-border)',
            background: 'var(--input-bg)',
            color: 'var(--text-color)',
            borderWidth: '1px',
            fontWeight: 500,
            flex: { xs: '1 1 100%', sm: '0 0 auto' },
            '&:hover': { borderColor: '#0a6fb4' },
          }}
        >
          Design New Insole
        </Button>}
      </div>
      <div className="admin-toolbar" style={{ display: showFilters ? 'flex' : 'none' }}>
        <Select
          value={selectedUserIdFilter ?? ''}
          onChange={(e) => handleUserSelectChange(e.target.value)}
          sx={{
            width: { xs: '100%', sm: '40%', md: '25%' },
            minWidth: 0,
            borderRadius: '4px',
            background: 'var(--input-bg)',
            color: 'var(--text-color)',
            fontSize: '0.9rem',
            fontWeight: 500,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            padding: '2px 10px',
            '& .MuiInputBase-input': { padding: '8px 8px 8px 0' },
            '& svg': { color: 'var(--text-color)' },
            '& fieldset': {
              border: '1px solid var(--input-border)',
            }
          }}
          displayEmpty={true}
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: 'var(--text-color)', opacity: 0.4 }}>Select User</span>;
            }
            const selectedUserItem = userSelectItems.find(usi => usi.id === parseInt(selected));
            if (selectedUserItem) {
              return <div>{selectedUserItem.name}<span style={{ color: 'gray', paddingInlineStart: '4px', fontSize: '0.9rem' }}>{selectedUserItem.username}</span></div>;
            } else return 'Invalid User selected!';
          }}
          // Limit visible items to 5 and enable scrollbar for the dropdown
          MenuProps={{
            PaperProps: {
              sx: {
                // 5 items * ~48px per item + small padding
                maxHeight: 48 * 5 + 8,
                background: 'var(--input-bg)',
              },
            },
            MenuListProps: {
              sx: {
                maxHeight: 48 * 5 + 8,
                overflowY: 'auto',
              },
            },
          }}
        >
          <MenuItem style={{ color: isDarkMode ? '#ffffff' : '#076596' }} value="">None</MenuItem>
          {userSelectItems.map(user => (
            <MenuItem style={{ color: isDarkMode ? '#ffffff' : '#076596' }} key={user.id} value={user.id.toString()}>
              {user.name}
              <span style={{ color: 'gray', paddingInlineStart: '4px', fontSize: '0.9rem' }}>{user.username}</span>
            </MenuItem>
          ))}
        </Select>
        {isOnlyFinalizedFilterEnabled && <FormControlLabel
          sx={{ alignItems: 'start', marginBottom: 0, minWidth: 0, width: 'fit-content' }}
          control={
            <Checkbox
              checked={onlyFinalizedFilter ?? false}
              onChange={(e) => { setOnlyFinalizedFilter(e.target.checked) }}
              sx={{ color: 'var(--text-color)' }}
            ></Checkbox>
          }
          label={<Typography sx={{ fontSize: 14, color: 'var(--text-color)', marginTop: '11px' }}>Only Completed</Typography>}
        >
        </FormControlLabel>}
        {isPrintedFilterEnabled && <FormControlLabel
          sx={{ alignItems: 'start', marginBottom: 0, minWidth: 0, width: 'fit-content' }}
          control={
            <Checkbox
              checked={isPrintedFilter ?? false}
              onChange={(e) => { setPrintedFilter(e.target.checked) }}
              sx={{ color: 'var(--text-color)' }}
            ></Checkbox>
          }
          label={<Typography sx={{ fontSize: 14, color: 'var(--text-color)', marginTop: '11px' }}>Only Printed</Typography>}
        >
        </FormControlLabel>}
        {sortColumnName !== null &&
          <Button
            variant="outlined"
            onClick={handleClearSort}
            startIcon={<CloseOutlined />}
            sx={{
              border: '1px solid var(--input-border)',
              background: 'var(--input-bg)',
              color: 'var(--text-color)',
              borderWidth: '1px',
              fontWeight: 500,
              '&:hover': { borderColor: '#0a6fb4' },
            }}
          >Clear Sort</Button>}
      </div>
      <TableContainer
        className="admin-table-scroll"
        component={Paper}
        sx={{
          backgroundColor: isDarkMode ? '#444444' : '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          border: isDarkMode ? '1px solid #076596' : '1px solid rgba(255, 255, 255, 0.3)',
          maxHeight: { xs: '60vh', md: '70vh' },
          overflow: 'auto',
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="designs table" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  backgroundColor: 'var(--accent-color)',
                  color: '#ffffff',
                  fontWeight: 700,
                  padding: '12px 16px',
                  fontSize: '0.95rem',
                  letterSpacing: '0.01em',
                  borderBottom: '1px solid var(--toolbar-border)',
                },
              }}
            >
              {authenticatedUser?.group?.IsHub && <TableCell>ID</TableCell>}
              <TableCell>DesignId<IconButton onClick={() => changeSort('id')}> {!sortDesc && sortColumnName === 'id' ? <UpIcon sx={{ color: 'white' }} /> : <DownIcon sx={{ color: 'white' }} />}</IconButton></TableCell>
              <TableCell>Name<IconButton onClick={() => changeSort('name')}> {!sortDesc && sortColumnName === 'name' ? <UpIcon sx={{ color: 'white' }} /> : <DownIcon sx={{ color: 'white' }} />}</IconButton></TableCell>
              <TableCell>Created By<IconButton onClick={() => changeSort('user')}> {!sortDesc && sortColumnName === 'user' ? <UpIcon sx={{ color: 'white' }} /> : <DownIcon sx={{ color: 'white' }} />}</IconButton></TableCell>
              <TableCell>Date<IconButton onClick={() => changeSort('createdat')}> {!sortDesc && sortColumnName === 'createdat' ? <UpIcon sx={{ color: 'white' }} /> : <DownIcon sx={{ color: 'white' }} />}</IconButton></TableCell>
              <TableCell>Sent Date</TableCell>
              <TableCell>Status<IconButton onClick={() => changeSort('status')}> {!sortDesc && sortColumnName === 'status' ? <UpIcon sx={{ color: 'white' }} /> : <DownIcon sx={{ color: 'white' }} />}</IconButton></TableCell>
              {!authenticatedUser?.group?.IsHub && (
                <TableCell>Print Count<IconButton onClick={() => changeSort('printcount')}> {!sortDesc && sortColumnName === 'printcount' ? <UpIcon sx={{ color: 'white' }} /> : <DownIcon sx={{ color: 'white' }} />}</IconButton></TableCell>
              )}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#076596',
                      fontSize: '1rem',
                      fontWeight: 500,
                      padding: '16px',
                    }}
                  >
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : error || designData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ff6b6b' : '#e74c3c',
                      fontSize: '1rem',
                      fontWeight: 500,
                      padding: '16px',
                    }}
                  >
                    {error || 'No matching designs found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              designData.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => handleRowClick(row.id)}
                  onDoubleClick={() => handleOpenDesign(row.id, row.taskId)}
                  selected={isSelected(row.id)}
                  sx={{
                    backgroundColor:
                      row.finalized != null
                        ? row.finalized
                          ? 'var(--table-row-bg)'
                          : 'var(--table-row-bg-highlight)'
                        : undefined,

                    '& .MuiTableCell-root': {
                      backgroundColor: 'transparent !important',
                    },

                    '&.MuiTableRow-hover:hover.MuiTableRow-hover:hover': {
                      backgroundColor: 'var(--table-row-bg-hover)',
                    },

                    '& td, & th': {
                      color: 'var(--text-color)',
                      padding: '10px 16px',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      borderBottom: isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.1)',
                    },

                    '&.Mui-selected': {
                      backgroundColor: 'var(--table-row-bg-focus)',
                    },

                    cursor: 'pointer',
                  }}
                >
                  {authenticatedUser?.group?.IsHub && <TableCell component="th" scope="row">{row.taskId}</TableCell>}
                  <TableCell component="th" scope="row">{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email ?? 'N/A'}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.sendDate}</TableCell>
                  <TableCell>
                    <span
                      className={`status-badge status-${row.status.toLowerCase()}`}
                      style={{
                        backgroundColor: row.hasWBCT && row.hasPressure ? 'rgba(230, 126, 34, 0.25)'
                          : row.hasPressure || row.hasRondom ? 'rgba(46, 204, 113, 0.25)'
                            : row.hasWBCT ? 'rgba(231, 76, 60, 0.5)'
                              : 'rgba(52, 152, 219, 0.25)',
                        color: 'var(--text-color)',
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {row.status}
                    </span>
                  </TableCell>
                  {!authenticatedUser?.group?.IsHub && (<TableCell>{row.printCount}</TableCell>)}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      aria-label="delete"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleDeleteDesign(row.id);
                      }}
                      sx={{ color: isDarkMode ? '#ffffff' : '#076596', '&:hover': { color: '#e74c3c' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="open"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDesign(row.id, row.taskId ?? -1);
                      }}
                      sx={{ color: isDarkMode ? '#ffffff' : '#076596', '&:hover': { color: '#0a6fb4' } }}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="admin-pagination" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            disabled={pageNumber === 1}
            onClick={handlePreviousPage}
            sx={{
              borderColor: '#076596',
              color: isDarkMode ? '#ffffff' : '#076596',
              backgroundColor: isDarkMode ? '#444444' : '#ffffff',
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
              '&.Mui-disabled': {
                borderColor: isDarkMode ? '#555555' : '#cccccc',
                color: isDarkMode ? '#777777' : '#999999',
              },
            }}
          >
            Prev
          </Button>
          {(() => {
            const total = totalPages.current;
            const pagesToShow: (number | 'ellipsis')[] = [];

            if (total <= 5) {
              for (let i = 1; i <= total; i++) pagesToShow.push(i);
            } else {
              if (pageNumber === 1) pagesToShow.push(1, 2, 'ellipsis', total - 1, total);
              else if (pageNumber && pageNumber < total - 1) pagesToShow.push(pageNumber - 1, pageNumber, 'ellipsis', total - 1, total);
              else if (pageNumber === total - 1) pagesToShow.push(1, 2, 'ellipsis', pageNumber, total)
              else if (pageNumber) pagesToShow.push(1, 2, 'ellipsis', pageNumber - 1, pageNumber)
            }

            return pagesToShow.map((p, idx) => {
              if (p === 'ellipsis') {
                return (<div key={`ellipsis-${idx}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end' }}> ... </div>);
              }

              const pageIndex = p as number;
              return (
                <Button
                  key={`page-${pageIndex}`}
                  disabled={pageIndex === pageNumber}
                  variant="outlined"
                  onClick={() => handleSelectPage(pageIndex)}
                  sx={{
                    borderColor: '#076596',
                    color: isDarkMode ? '#ffffff' : '#076596',
                    backgroundColor: isDarkMode ? '#444444' : '#ffffff',
                    padding: '8px 16px',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
                    '&.Mui-disabled': {
                      borderColor: isDarkMode ? '#555555' : '#cccccc',
                      color: isDarkMode ? '#777777' : '#999999',
                    },
                  }}
                >
                  {pageIndex}
                </Button>
              );
            });
          })()}
          <Button
            variant="outlined"
            disabled={!hasNextPage}
            onClick={handleNextPage}
            sx={{
              borderColor: '#076596',
              color: isDarkMode ? '#ffffff' : '#076596',
              backgroundColor: isDarkMode ? '#444444' : '#ffffff',
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
              '&.Mui-disabled': {
                borderColor: isDarkMode ? '#555555' : '#cccccc',
                color: isDarkMode ? '#777777' : '#999999',
              },
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DesignsTable;